import { useEffect, useRef, useState } from "react";
import { Move, RotateCcw } from "lucide-react";
import { mediaAbs } from "@/lib/media";
import { DEFAULT_FRAMING, frameStyle } from "@/lib/framing";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

/** Drag/pinch/zoom frame with the exact display aspect. Edits one framing value. */
const Frame = ({ src, aspect, f, onChange, testid }) => {
  const ref = useRef(null);
  const nat = useRef({ w: 1, h: 1 });
  const ptrs = useRef(new Map());
  const pinch = useRef(null);
  const move = (dxPx, dyPx) => {
    const r = ref.current.getBoundingClientRect();
    const s = Math.max(r.width / nat.current.w, r.height / nat.current.h);
    const ox = Math.max(nat.current.w * s * f.zoom - r.width, r.width * 0.6);
    const oy = Math.max(nat.current.h * s * f.zoom - r.height, r.height * 0.6);
    onChange({ ...f, x: clamp(f.x - dxPx / ox, 0, 1), y: clamp(f.y - dyPx / oy, 0, 1) });
  };
  const down = (e) => {
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { ref.current.setPointerCapture(e.pointerId); } catch { /* pointer already released */ }
  };
  const up = (e) => { ptrs.current.delete(e.pointerId); pinch.current = null; };
  const pmove = (e) => {
    const prev = ptrs.current.get(e.pointerId);
    if (!prev) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current) onChange({ ...f, zoom: clamp(f.zoom * (d / pinch.current), 1, 3) });
      pinch.current = d;
      return;
    }
    move(e.clientX - prev.x, e.clientY - prev.y);
  };
  const key = (e) => {
    const k = { ArrowLeft: [-8, 0], ArrowRight: [8, 0], ArrowUp: [0, -8], ArrowDown: [0, 8] }[e.key];
    if (k) { e.preventDefault(); move(-k[0], -k[1]); }
    const zin = ["+", "=", "Add"].includes(e.key) || e.code === "NumpadAdd";
    const zout = ["-", "−", "_", "Subtract"].includes(e.key) || e.code === "NumpadSubtract";
    if (zin || zout) { e.preventDefault(); onChange({ ...f, zoom: clamp(f.zoom + (zin ? 0.1 : -0.1), 1, 3) }); }
  };
  return (
    <div ref={ref} style={{ aspectRatio: aspect, touchAction: "none" }} tabIndex={0} role="application"
         aria-label="Encuadre: arrastra para mover, flechas para ajustar, + y − para zoom"
         onPointerDown={down} onPointerMove={pmove} onPointerUp={up} onPointerCancel={up} onKeyDown={key}
         className="relative w-full cursor-grab select-none overflow-hidden rounded-xl bg-slate-200 ring-[#1E6FD9] focus:outline-none focus-visible:ring-2 active:cursor-grabbing"
         data-testid={testid}>
      <img src={mediaAbs(src)} alt="" draggable={false} onLoad={(e) => { nat.current = { w: e.target.naturalWidth, h: e.target.naturalHeight }; }}
           className="pointer-events-none h-full w-full object-cover" style={frameStyle(f)} />
      <span className="pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white">
        <Move className="h-3 w-3" /> Arrastra para mover
      </span>
    </div>
  );
};

/**
 * targets: [{ key, label, aspect }] — framings edited (e.g. escritorio/móvil).
 * previews: [{ label, aspect, target }] — small live previews using a target's framing.
 */
export const FramingEditor = ({ open, src, value, targets, previews = [], onSave, onClose, title = "Ajustar encuadre", renderCard }) => {
  const [draft, setDraft] = useState({});
  const [active, setActive] = useState(targets[0].key);
  useEffect(() => {
    if (open) { setDraft(Object.fromEntries(targets.map((t) => [t.key, { ...DEFAULT_FRAMING, ...(value[t.key] || value[targets[0].key] || {}) }]))); setActive(targets[0].key); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const t = targets.find((x) => x.key === active);
  const f = draft[active];
  if (!open || !f) return null;
  const set = (nf) => setDraft((d) => ({ ...d, [active]: nf }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] overflow-y-auto rounded-2xl sm:max-w-xl" data-testid="framing-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">{title}</DialogTitle>
          <DialogDescription>La imagen original no se modifica; solo cambia cómo se muestra.</DialogDescription>
        </DialogHeader>
        {targets.length > 1 && (
          <div className="flex gap-2" role="tablist">
            {targets.map((x) => (
              <button key={x.key} type="button" role="tab" aria-selected={active === x.key} onClick={() => setActive(x.key)}
                      className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${active === x.key ? "bg-[#0B2545] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                      data-testid={`framing-tab-${x.key}`}>{x.label}</button>
            ))}
          </div>
        )}
        <div className={t.narrow ? "mx-auto w-full max-w-[260px]" : ""}>
          <Frame src={src} aspect={t.aspect} f={f} onChange={set} testid="framing-frame" />
        </div>
        <div className="flex items-center gap-3">
          <label htmlFor="framing-zoom" className="shrink-0 text-sm font-medium text-slate-700">Zoom</label>
          <Slider id="framing-zoom" min={100} max={300} step={5} value={[Math.round(f.zoom * 100)]} onValueChange={([z]) => set({ ...f, zoom: z / 100 })}
                  thumbLabel="Zoom del encuadre" className="flex-1" data-testid="framing-zoom" />
          <span className="w-12 text-right text-sm tabular-nums text-slate-600" data-testid="framing-zoom-value">{Math.round(f.zoom * 100)}%</span>
        </div>
        {(previews.length > 0 || renderCard) && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Vista previa</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {renderCard && <div data-testid="framing-preview-card">{renderCard(draft[targets[0].key])}</div>}
              {previews.map((p) => (
                <figure key={p.label} className={p.narrow ? "mx-auto w-2/3" : ""} data-testid={`framing-preview-${p.target}`}>
                  <div style={{ aspectRatio: p.aspect }} className="overflow-hidden rounded-lg bg-slate-200">
                    <img src={mediaAbs(src)} alt="" className="h-full w-full object-cover" style={frameStyle(draft[p.target] || f)} />
                  </div>
                  <figcaption className="mt-1 text-xs text-slate-500">{p.label}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap justify-between gap-2">
          <Button type="button" variant="outline" onClick={() => set({ ...DEFAULT_FRAMING })} className="rounded-xl" data-testid="framing-reset">
            <RotateCcw className="mr-1.5 h-4 w-4" /> Restablecer
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl" data-testid="framing-cancel">Cancelar</Button>
            <Button type="button" onClick={() => { onSave(draft); onClose(); }} className="rounded-xl bg-[#1E6FD9]" data-testid="framing-save">Guardar encuadre</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
